'use client';
import React, { useState } from 'react';
import { useSnapshot } from 'valtio';
import { appState, userState } from '../store';
import useApi from '../hooks/useApi';
import { LoginRequest } from '../@backend';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/ds/Input';
import { Btn } from '../ui/ds/Btn';
import { Badge } from '../ui/ds/Badge';

export default function LoginModal() {
  const appSnap  = useSnapshot(appState);
  const navigate = useNavigate();

  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isSuccess, setIsSuccess] = useState(false);
  const { backend } = useApi(true);

  if (!appSnap.modals.showLoginModal) return null;

  const close = () => { appState.modals.showLoginModal = false; };

  const handleForgotPassword = () => {
    close();
    navigate('/forgot-password');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors([]);
    try {
      const loginRes = await backend.user.userControllerLogin({ email, password } as LoginRequest);
      if (loginRes.token) {
        userState.token       = loginRes.token;
        userState.isLoggedIn  = true;
        userState.userProfile = loginRes.userProfile;
        localStorage.setItem('token', loginRes.token);
        setIsSuccess(true);

        if (!loginRes.userProfile?.entryFeePaid) {
          navigate('/uzivatel/poplatek');
          close();
          return;
        }
        setTimeout(() => close(), 800);
      }
    } catch (e: any) {
      if (e.status === 401) {
        setFormErrors(['Neplatné přihlašovací údaje']);
      } else {
        setFormErrors(['Přihlášení selhalo. Zkuste to prosím znovu.']);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={close}
    >
      <div
        className="bg-bg-card rounded-hero shadow-card w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-header px-6 py-5">
          <h2 className="tb-h2 text-white">Přihlášení</h2>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-1">
          {isSuccess && (
            <div className="flex items-center gap-3 bg-semantic-success/10 border border-semantic-success/20 rounded-content px-4 py-3 mb-2">
              <Badge variant="success">Úspěch</Badge>
              <span className="tb-body text-semantic-success">Přihlášení proběhlo úspěšně!</span>
            </div>
          )}
          {formErrors.length > 0 && (
            <div className="flex items-center gap-3 bg-semantic-danger/10 border border-semantic-danger/20 rounded-content px-4 py-3 mb-2">
              <Badge variant="danger">Chyba</Badge>
              <span className="tb-body text-semantic-danger">{formErrors.join(', ')}</span>
            </div>
          )}

          <Input
            label="Email"
            type="email"
            id="login-email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <Input
            label="Heslo"
            type="password"
            id="login-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />

          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="tb-caption text-brand-blue hover:underline"
            >
              Zapomenuté heslo?
            </button>
          </div>

          {/* Footer */}
          <div className="flex gap-3">
            <Btn type="button" variant="ghost" size="sm" fullWidth onClick={close}>
              Zrušit
            </Btn>
            <Btn type="submit" variant="primary" size="sm" fullWidth>
              Přihlásit
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}
